import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Lightbox } from "./Lightbox";

import API from "../apiBase";

/* Messaggio a metà percorso, tra un blocco e l'altro */
export const INTERLUDIO = [
  "Mi ricordo quando mi hai raccontato cosa sentivi in “Figli delle Stelle”: quella malinconia, ma anche la speranza di ritrovarsi, da qualche parte. Mi è rimasto dentro quel pensiero, e ascoltandola torno anche a quelle tue parole.",
  "Ripensando a noi, mi accorgo che ci sono cose che avrei potuto capire meglio, momenti in cui avrei potuto ascoltarti di più. Oggi vorrei riuscire a farlo, lasciando spazio a quello che senti, anche quando è diverso da ciò che spero.",
  "Questa pagina è un modo per condividere con te qualcosa che per me conta ancora.",
];

/* La carrellata raccoglie in automatico TUTTE le foto e i video
   delle cartelle media/tappa1..4, nell'ordine dei didascalie.txt.
   Per cambiare ordine o frasi si modificano quei file come sempre.
   Toccando una foto si apre a schermo intero (frecce o tastiera ← →). */

const MediaItem = ({ item, index, onOpen }) => {
  const videoRef = useRef(null);
  const itemRef = useRef(null);
  const isVideo = item.type === "video";
  const side = index % 2 === 0 ? "left" : "right";

  /* deriva dolce mentre scorri: ogni foto respira con la pagina */
  const { scrollYProgress } = useScroll({
    target: itemRef,
    offset: ["start end", "end start"],
  });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [36, -36]);

  /* i video partono da soli in muto quando entrano nello schermo
     (l'audio automatico è vietato dai browser prima di un tocco) */
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) v.play().catch(() => {});
        else v.pause();
      },
      { threshold: 0.45 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);

  return (
    <motion.figure
      ref={itemRef}
      className={`carrellata-item carrellata-item--${side}`}
      style={{ y: parallaxY }}
      initial={{ opacity: 0, scale: 0.97 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, margin: "-70px" }}
      transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`carrellata-item-${index}`}
    >
      <div className="media-spotlight carrellata-spotlight">
        <div
          className="media-frame carrellata-frame carrellata-frame--zoom"
          onClick={onOpen}
          role="button"
          aria-label={isVideo ? "Apri il video a schermo intero" : "Apri la foto a schermo intero"}
        >
          {isVideo ? (
            <div
              className="video-shell"
              onMouseEnter={() => videoRef.current?.setAttribute("controls", "")}
              onMouseLeave={() => videoRef.current?.removeAttribute("controls")}
            >
              <video
                ref={videoRef}
                src={encodeURI(item.src)}
                poster={item.poster ? encodeURI(item.poster) : undefined}
                preload="metadata"
                muted
                loop
                playsInline
                data-testid={`carrellata-video-${index}`}
              />
            </div>
          ) : (
            <img
              src={encodeURI(item.src)}
              alt={item.caption || `Ricordo ${index + 1}`}
              loading="lazy"
              data-testid={`carrellata-image-${index}`}
            />
          )}
        </div>
      </div>
      <figcaption className="carrellata-caption">
        <span className="carrellata-num">{String(index + 1).padStart(2, "0")}</span>
        {(item.caption || item.date) && (
          <span className="carrellata-info">
            {item.caption && (
              <span className="carrellata-text" data-testid={`carrellata-caption-${index}`}>
                {item.caption}
              </span>
            )}
            {item.date && (
              <span className="carrellata-date" data-testid={`carrellata-date-${index}`}>
                {item.date}
              </span>
            )}
          </span>
        )}
      </figcaption>
    </motion.figure>
  );
};

export const Carrellata = () => {
  const [blocks, setBlocks] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    Promise.all(
      [1, 2, 3, 4].map((n) =>
        fetch(`${API}/api/media/${n}`)
          .then((r) => (r.ok ? r.json() : { items: [], nome: "" }))
          .catch(() => ({ items: [], nome: "" }))
      )
    ).then((results) =>
      setBlocks(results.map((r) => ({ nome: r.nome || "", items: r.items })))
    );
  }, []);

  const items = blocks ? blocks.flatMap((b) => b.items) : null;
  const media = items || [];

  const openItem = (src) => {
    document.querySelectorAll(".carrellata video").forEach((v) => v.pause());
    const i = media.findIndex((m) => m.src === src);
    if (i >= 0) setLightbox(i);
  };

  const feed = [];
  let num = 0;
  (blocks || []).forEach((b, bi) => {
    if (b.nome && b.items.length) {
      feed.push({ kind: "divider", nome: b.nome, key: `divider-${bi}` });
    }
    b.items.forEach((it) => {
      feed.push({ kind: "item", item: it, index: num, key: it.src });
      num += 1;
    });
    if (bi === 1) {
      feed.push({ kind: "interludio", key: "interludio" });
    }
  });

  return (
    <main className="carrellata" data-testid="carrellata">
      <motion.div
        className="carrellata-head"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      >
        <p className="carrellata-eyebrow">la nostra storia in foto</p>
        <h2 className="carrellata-title">Ogni foto, una stella.</h2>
        <p className="carrellata-hint">tocca una foto per vederla da vicino</p>
      </motion.div>
      {feed.map((f) =>
        f.kind === "divider" ? (
          <motion.div
            key={f.key}
            className="carrellata-divider"
            data-testid={`carrellata-${f.key}`}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1 }}
          >
            <span className="carrellata-divider-line" />
            <span className="carrellata-divider-label">{f.nome}</span>
            <span className="carrellata-divider-line" />
          </motion.div>
        ) : f.kind === "interludio" ? (
          <motion.div
            key={f.key}
            className="interludio"
            data-testid="interludio"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="interludio-star">✦</span>
            {INTERLUDIO.map((p, i) => (
              <p key={i} className={i === INTERLUDIO.length - 1 ? "interludio-last" : ""}>
                {p}
              </p>
            ))}
            <span className="interludio-star">✦</span>
          </motion.div>
        ) : (
          <MediaItem
            key={f.key}
            item={f.item}
            index={f.index}
            onOpen={() => openItem(f.item.src)}
          />
        )
      )}

      <Lightbox
        media={media}
        index={lightbox}
        onClose={() => setLightbox(null)}
        onNav={setLightbox}
      />
    </main>
  );
};
